import { useState } from 'react'
import { useForm } from '../../contexts/FormContext'
import { Button, Badge, SaveButton } from '../../components/common'
import { Input, Textarea, Radio, FileUpload } from '../../components/forms'
import './Step4Pages.css'

const PAGE_TYPES = [
  { value: 'home', label: 'Home Page', disabled: true },
  { value: 'about', label: 'About / Company' },
  { value: 'contact', label: 'Contact Page' },
  { value: 'services', label: 'Services' },
  { value: 'team', label: 'Team / Staff' },
  { value: 'portfolio', label: 'Portfolio / Case Studies' },
  { value: 'shop', label: 'Shop / Products' },
  { value: 'blog', label: 'Blog / News' },
  { value: 'faq', label: 'FAQ' },
  { value: 'privacy', label: 'Privacy Policy' },
  { value: 'terms', label: 'Terms of Service' },
  { value: 'careers', label: 'Recruitment / Careers' },
  { value: 'legal', label: 'Commercial Transactions Act' },
  { value: 'custom', label: 'Custom Page' }
]

function Step4Pages() {
  const { formData, updateFormData, updateNestedFormData, goToNextStep, goToPreviousStep } = useForm()
  const [expandedPageId, setExpandedPageId] = useState(null)
  const [showAddPage, setShowAddPage] = useState(false)
  const [newPage, setNewPage] = useState({
    type: 'services',
    name: '',
    complexity: 'basic',
    specifications: ''
  })

  // Toggle page edit section
  const togglePageEdit = (pageId) => {
    setExpandedPageId(expandedPageId === pageId ? null : pageId)
  }

  // Update default page field
  const updateDefaultPage = (pageId, field, value) => {
    const updatedPages = formData.pages.default.map(page =>
      page.id === pageId ? { ...page, [field]: value } : page
    )
    updateNestedFormData('pages.default', updatedPages)
  }

  // Update default page complexity
  const updateDefaultPageComplexity = (pageId, complexity) => {
    const cost = complexity === 'advanced' ? 5000 : 0
    const timelineDays = complexity === 'advanced' ? 1 : 0

    const updatedPages = formData.pages.default.map(page => {
      if (page.id === pageId) {
        const oldCost = page.cost || 0
        const oldDays = page.timelineDays || 0

        // Update global costs
        updateFormData({
          costs: {
            ...formData.costs,
            setupTotal: formData.costs.setupTotal - oldCost + cost
          },
          timeline: {
            ...formData.timeline,
            estimatedDays: formData.timeline.estimatedDays - oldDays + timelineDays
          }
        })

        return { ...page, complexity, cost, timelineDays }
      }
      return page
    })

    updateNestedFormData('pages.default', updatedPages)
  }

  // Save default page configuration
  const saveDefaultPage = (pageId) => {
    const page = formData.pages.default.find(p => p.id === pageId)

    if (!page.complexity) {
      alert('Please select a complexity level')
      return
    }

    if (!page.specifications || !page.specifications.trim()) {
      alert('Please provide content specifications')
      return
    }

    updateDefaultPage(pageId, 'configured', true)
    setExpandedPageId(null)
  }

  // Add additional page
  const handleAddPage = () => {
    if (!newPage.name) {
      alert('Please enter a page name')
      return
    }

    const pageCost = newPage.complexity === 'basic' ? 5000 : 10000
    const timelineDays = newPage.complexity === 'basic' ? 1 : 2

    const additionalPage = {
      id: `page-${Date.now()}`,
      ...newPage,
      cost: pageCost,
      timelineDays,
      files: []
    }

    updateFormData({
      pages: {
        ...formData.pages,
        additional: [...formData.pages.additional, additionalPage]
      },
      costs: {
        ...formData.costs,
        setupTotal: formData.costs.setupTotal + pageCost
      },
      timeline: {
        ...formData.timeline,
        estimatedDays: formData.timeline.estimatedDays + timelineDays
      }
    })

    // Reset form
    setNewPage({
      type: 'services',
      name: '',
      complexity: 'basic',
      specifications: ''
    })
    setShowAddPage(false)
  }

  // Remove additional page
  const handleRemovePage = (pageId) => {
    const pageToRemove = formData.pages.additional.find(p => p.id === pageId)
    if (!pageToRemove) return

    updateFormData({
      pages: {
        ...formData.pages,
        additional: formData.pages.additional.filter(p => p.id !== pageId)
      },
      costs: {
        ...formData.costs,
        setupTotal: formData.costs.setupTotal - pageToRemove.cost
      },
      timeline: {
        ...formData.timeline,
        estimatedDays: formData.timeline.estimatedDays - pageToRemove.timelineDays
      }
    })
  }

  // Check if can proceed
  const allDefaultPagesConfigured = formData.pages.default.every(page => page.configured)
  const canProceed = allDefaultPagesConfigured

  const handleNext = () => {
    if (!allDefaultPagesConfigured) {
      const unconfigured = formData.pages.default.find(p => !p.configured)
      alert(`Please configure all default pages. "${unconfigured.name}" still needs setup.`)
      return
    }
    goToNextStep()
  }

  return (
    <div className="step-section">
      {/* Section Header */}
      <div className="section-header">
        <h2>Step 4: Configure Your Website Pages</h2>
        <p className="section-subtitle">Setup all pages with content specifications</p>
      </div>

      {/* Section Body */}
      <div className="section-body">
        {/* Info Box */}
        <div className="info-box-warning">
          <div className="info-box-title">⚠️ Action Required: Configure Your Pages</div>
          <p><strong>Please edit each default page below</strong> to provide content specifications. This helps us build exactly what you need. You can also change the page type for most pages (except Home Page).</p>
        </div>

        <div className="complexity-hint">
          <strong>💡 Page Complexity:</strong> <strong>Basic pages</strong> are included with simple content. <strong>Advanced pages</strong> on default pages add +¥5,000 each.
        </div>

        {/* Default Pages */}
        <div className="default-pages-section">
          <h3 className="section-subtitle-h3">CONFIGURE YOUR PAGES</h3>

          <div className="pages-list">
            {formData.pages.default.map(page => (
              <div
                key={page.id}
                className={`page-config-card ${page.configured ? 'configured' : 'incomplete'}`}
              >
                <div className="page-card-header" onClick={() => togglePageEdit(page.id)}>
                  <div className="page-header-left">
                    <span className="page-icon">{page.icon}</span>
                    <div className="page-info">
                      <div className="page-name">{page.name}</div>
                      <div className="page-desc">{page.desc}</div>
                    </div>
                  </div>
                  <div className="page-header-right">
                    <Badge variant={page.configured ? 'success' : 'warning'} className="page-badge">
                      {page.configured ? 'Configured' : 'Setup Required'}
                    </Badge>
                    <Button
                      variant={page.configured ? 'secondary' : 'primary'}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        togglePageEdit(page.id)
                      }}
                    >
                      {page.configured ? 'Edit' : 'Setup Page'}
                    </Button>
                  </div>
                </div>

                {/* Expandable Edit Section */}
                {expandedPageId === page.id && (
                  <div className="page-edit-section">
                    {page.locked && (
                      <div className="lock-notice">
                        <span>🔒</span>
                        <span>Home Page type is fixed and cannot be changed</span>
                      </div>
                    )}

                    {!page.locked && (
                      <div className="form-group">
                        <label className="form-label">Change Page Type (Optional)</label>
                        <select
                          className="select"
                          value={page.type}
                          onChange={(e) => updateDefaultPage(page.id, 'type', e.target.value)}
                        >
                          {PAGE_TYPES.map(type => (
                            <option key={type.value} value={type.value} disabled={type.disabled}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="form-group">
                      <label className="form-label">Complexity <span className="required">*</span></label>
                      <div className="complexity-grid">
                        <label className={`complexity-card ${page.complexity === 'basic' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name={`complexity-${page.id}`}
                            value="basic"
                            checked={page.complexity === 'basic'}
                            onChange={() => updateDefaultPageComplexity(page.id, 'basic')}
                          />
                          <div className="complexity-content">
                            <div className="complexity-title">Basic Page</div>
                            <div className="complexity-price">Included</div>
                            <div className="complexity-desc">Simple content with text and images</div>
                          </div>
                        </label>
                        <label className={`complexity-card ${page.complexity === 'advanced' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name={`complexity-${page.id}`}
                            value="advanced"
                            checked={page.complexity === 'advanced'}
                            onChange={() => updateDefaultPageComplexity(page.id, 'advanced')}
                          />
                          <div className="complexity-content">
                            <div className="complexity-title">Advanced Page</div>
                            <div className="complexity-price">+¥5,000</div>
                            <div className="complexity-desc">Complex features or custom functionality</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <Textarea
                      id={`specs-${page.id}`}
                      label="Content Specifications"
                      required
                      value={page.specifications}
                      onChange={(e) => updateDefaultPage(page.id, 'specifications', e.target.value)}
                      placeholder="Describe what content should be on this page, key sections, features needed..."
                      rows={4}
                      maxLength={500}
                      showCount
                    />

                    <FileUpload
                      id={`page-files-${page.id}`}
                      label="Attach Reference / Specification Documents"
                      accept=".pdf,.doc,.docx,.jpg,.png,.ai,.psd"
                      multiple
                      uploadType="page"
                      uploadId={page.id}
                      files={page.files || []}
                      onChange={(files) => updateDefaultPage(page.id, 'files', files)}
                      helperText="PDF, Word, Images, mockups, etc."
                    />

                    <div className="page-edit-actions">
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => saveDefaultPage(page.id)}
                      >
                        Save & Close
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Additional Pages */}
        <div className="additional-pages-section">
          <div className="section-header-with-action">
            <h3 className="section-subtitle-h3">ADDITIONAL PAGES</h3>
            {!showAddPage && (
              <Button variant="secondary" size="sm" onClick={() => setShowAddPage(true)}>
                + Add Page
              </Button>
            )}
          </div>

          {/* Added Pages List */}
          {formData.pages.additional.length > 0 && (
            <div className="pages-list">
              {formData.pages.additional.map((page, index) => (
                <div
                  key={page.id}
                  className="page-config-card configured"
                >
                  <div className="page-card-header" onClick={() => togglePageEdit(`additional-${page.id}`)}>
                    <div className="page-header-left">
                      <span className="page-icon">📄</span>
                      <div className="page-info">
                        <div className="page-name">{page.name}</div>
                        <div className="page-desc">{page.type} • {page.complexity === 'basic' ? 'Basic (+¥5k)' : 'Advanced (+¥10k)'}</div>
                      </div>
                    </div>
                    <div className="page-header-right">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          togglePageEdit(`additional-${page.id}`)
                        }}
                      >
                        Edit
                      </Button>
                      <button
                        className="remove-page-btn-inline"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemovePage(page.id)
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {/* Expandable Edit Section for Additional Pages */}
                  {expandedPageId === `additional-${page.id}` && (
                    <div className="page-edit-section">
                      <div className="form-group">
                        <label className="form-label">Page Type</label>
                        <select
                          className="select"
                          value={page.type}
                          onChange={(e) => {
                            const updatedPages = formData.pages.additional.map(p =>
                              p.id === page.id ? { ...p, type: e.target.value } : p
                            )
                            updateNestedFormData('pages.additional', updatedPages)
                          }}
                        >
                          {PAGE_TYPES.filter(t => !t.disabled).map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>

                      <Input
                        id={`additional-name-${page.id}`}
                        label="Page Name"
                        value={page.name}
                        onChange={(e) => {
                          const updatedPages = formData.pages.additional.map(p =>
                            p.id === page.id ? { ...p, name: e.target.value } : p
                          )
                          updateNestedFormData('pages.additional', updatedPages)
                        }}
                      />

                      <div className="form-group">
                        <label className="form-label">Complexity</label>
                        <div className="complexity-grid">
                          <label className={`complexity-card ${page.complexity === 'basic' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name={`complexity-additional-${page.id}`}
                              value="basic"
                              checked={page.complexity === 'basic'}
                              onChange={() => {
                                const oldCost = page.cost
                                const oldDays = page.timelineDays
                                const updatedPages = formData.pages.additional.map(p =>
                                  p.id === page.id ? { ...p, complexity: 'basic', cost: 5000, timelineDays: 1 } : p
                                )
                                updateNestedFormData('pages.additional', updatedPages)
                                updateFormData({
                                  costs: {
                                    ...formData.costs,
                                    setupTotal: formData.costs.setupTotal - oldCost + 5000
                                  },
                                  timeline: {
                                    ...formData.timeline,
                                    estimatedDays: formData.timeline.estimatedDays - oldDays + 1
                                  }
                                })
                              }}
                            />
                            <div className="complexity-content">
                              <div className="complexity-title">Basic Page</div>
                              <div className="complexity-price">+¥5,000</div>
                              <div className="complexity-desc">Simple content page</div>
                            </div>
                          </label>
                          <label className={`complexity-card ${page.complexity === 'advanced' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name={`complexity-additional-${page.id}`}
                              value="advanced"
                              checked={page.complexity === 'advanced'}
                              onChange={() => {
                                const oldCost = page.cost
                                const oldDays = page.timelineDays
                                const updatedPages = formData.pages.additional.map(p =>
                                  p.id === page.id ? { ...p, complexity: 'advanced', cost: 10000, timelineDays: 2 } : p
                                )
                                updateNestedFormData('pages.additional', updatedPages)
                                updateFormData({
                                  costs: {
                                    ...formData.costs,
                                    setupTotal: formData.costs.setupTotal - oldCost + 10000
                                  },
                                  timeline: {
                                    ...formData.timeline,
                                    estimatedDays: formData.timeline.estimatedDays - oldDays + 2
                                  }
                                })
                              }}
                            />
                            <div className="complexity-content">
                              <div className="complexity-title">Advanced Page</div>
                              <div className="complexity-price">+¥10,000</div>
                              <div className="complexity-desc">Complex features</div>
                            </div>
                          </label>
                        </div>
                      </div>

                      <Textarea
                        id={`additional-specs-${page.id}`}
                        label="Content Specifications"
                        value={page.specifications}
                        onChange={(e) => {
                          const updatedPages = formData.pages.additional.map(p =>
                            p.id === page.id ? { ...p, specifications: e.target.value } : p
                          )
                          updateNestedFormData('pages.additional', updatedPages)
                        }}
                        rows={4}
                        maxLength={500}
                        showCount
                      />

                      <FileUpload
                        id={`additional-page-files-${page.id}`}
                        label="Attach Reference / Specification Documents"
                        accept=".pdf,.doc,.docx,.jpg,.png,.ai,.psd"
                        multiple
                        uploadType="page"
                        uploadId={page.id}
                        files={page.files || []}
                        onChange={(files) => {
                          const updatedPages = formData.pages.additional.map(p =>
                            p.id === page.id ? { ...p, files } : p
                          )
                          updateNestedFormData('pages.additional', updatedPages)
                        }}
                        helperText="PDF, Word, Images, mockups, etc."
                      />

                      <div className="page-edit-actions">
                        <Button
                          variant="primary"
                          size="md"
                          onClick={() => setExpandedPageId(null)}
                        >
                          Save & Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add Page Form */}
          {showAddPage && (
            <div className="add-page-form-card">
              <h4 className="form-card-title">New Additional Page</h4>

              <div className="form-grid-2col">
                <div className="form-group">
                  <label className="form-label">Page Type <span className="required">*</span></label>
                  <select
                    className="select"
                    value={newPage.type}
                    onChange={(e) => setNewPage({ ...newPage, type: e.target.value })}
                  >
                    {PAGE_TYPES.filter(t => !t.disabled).map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <Input
                  id="new-page-name"
                  label="Page Name"
                  required
                  value={newPage.name}
                  onChange={(e) => setNewPage({ ...newPage, name: e.target.value })}
                  placeholder="e.g., Our Services"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Complexity <span className="required">*</span></label>
                <div className="complexity-grid">
                  <label className={`complexity-card ${newPage.complexity === 'basic' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="new-complexity"
                      value="basic"
                      checked={newPage.complexity === 'basic'}
                      onChange={() => setNewPage({ ...newPage, complexity: 'basic' })}
                    />
                    <div className="complexity-content">
                      <div className="complexity-title">Basic Page</div>
                      <div className="complexity-price">+¥5,000</div>
                      <div className="complexity-desc">Simple content page</div>
                    </div>
                  </label>
                  <label className={`complexity-card ${newPage.complexity === 'advanced' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="new-complexity"
                      value="advanced"
                      checked={newPage.complexity === 'advanced'}
                      onChange={() => setNewPage({ ...newPage, complexity: 'advanced' })}
                    />
                    <div className="complexity-content">
                      <div className="complexity-title">Advanced Page</div>
                      <div className="complexity-price">+¥10,000</div>
                      <div className="complexity-desc">Complex features</div>
                    </div>
                  </label>
                </div>
              </div>

              <Textarea
                id="new-page-specs"
                label="Content Specifications"
                value={newPage.specifications}
                onChange={(e) => setNewPage({ ...newPage, specifications: e.target.value })}
                rows={3}
                maxLength={500}
                showCount
                placeholder="Describe what content this page should have"
              />

              <div className="add-page-actions">
                <Button variant="secondary" size="sm" onClick={() => setShowAddPage(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddPage}
                  disabled={!newPage.name}
                >
                  Save Page
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Section Actions */}
      <div className="section-actions">
        <Button variant="secondary" size="md" onClick={goToPreviousStep}>
          ← Back
        </Button>
        <div className="actions-right">
          <SaveButton />
          <Button
            variant="primary"
            size="md"
            disabled={!canProceed}
            onClick={handleNext}
          >
            Next Step
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Step4Pages
